import sanitizeHtml from 'sanitize-html';
import { KoobLocationSearch as KoobLocationSearchBase } from '@koob/ui';
import search from '../api';
import { SEARCH_DEFAULT_TYPES, SEARCH_MIN_LENGTH } from '../constants';
import { SEARCH_TYPES } from '../enums';

const EXPERIENCE_TYPES = [
  SEARCH_TYPES.EXPERIENCE,
  SEARCH_TYPES.ACTIVITY,
  SEARCH_TYPES.PROGRAM,
  SEARCH_TYPES.TRANSFER,
];

// The index returns four flavours of experience and one of template; every
// consumer only ever branches on the two normalized kinds.
const normalizeKind = (type) =>
  EXPERIENCE_TYPES.includes(type)
    ? 'experience'
    : type === SEARCH_TYPES.TEMPLATE
      ? 'template'
      : type;

// Hotel filter APIs (getHotels & co) expect plural kinds as filter keys, so
// those call sites ask for the kind pluralized and use it as an object key.
const PLURAL_KINDS = {
  country: 'countries',
  region: 'regions',
  city: 'cities',
  hotel: 'hotels',
};

const KIND_ORDER = [
  'country',
  'region',
  'city',
  'hotel',
  'experience',
  'template',
];

export const GROUP_LABELS = {
  country: 'Countries',
  region: 'Regions',
  city: 'Cities',
  hotel: 'Hotels',
  experience: 'Experiences',
  template: 'Templates',
  countries: 'Countries',
  regions: 'Regions',
  cities: 'Cities',
  hotels: 'Hotels',
};

// Duplicated verbatim in both apps' utils/index.js, which a package can't
// import. The parseInt short-circuit means an already-numeric id passes through.
const decodeId = (idKoob) => {
  if (!idKoob) return null;
  const numeric = parseInt(idKoob);
  if (!isNaN(numeric)) return numeric;
  const decoded = atob(String(idKoob).replace('\n', ''));
  return parseInt(decoded.substring(decoded.lastIndexOf('/') + 1));
};

const stripHtml = (value) =>
  sanitizeHtml(value ?? '', {
    allowedTags: [],
    allowedAttributes: {},
  })?.replace(/&amp;/g, '&');

/**
 * Location search bound to the koob search index. The presentation lives in
 * @koob/ui — this only owns what is app-specific: which types to query, how to
 * normalize the index's kinds, and which results to drop.
 */
export default function KoobLocationSearch({
  types = SEARCH_DEFAULT_TYPES,
  kinds,
  pluralKinds = false,
  excludedExperienceIds,
  fromOrganizationId,
  isMarketPlaceSource,
  searchedCountries,
  shouldSearchAdditionalFields,
  locale,
  // KD-3547 — opt in to products of every state (available, in progress, stand
  // by). Off by default so booking-facing searches keep listing bookable
  // products only.
  includeUnavailable,
  groupLabels = GROUP_LABELS,
  onValueChange,
  ...props
}) {
  // Not memoised on purpose: the base component reads fetchResults through a
  // ref and only re-runs on query change, so a fresh identity costs nothing.
  const fetchResults = (query, page) =>
    search({
      query,
      // Several call sites pass a nested array ([DEFAULTS, 'hotel']); flat()
      // makes that explicit instead of relying on Array#join's flattening.
      types: types.flat(),
      fromOrganizationId: decodeId(fromOrganizationId),
      isMarketPlaceSource,
      searchedCountries,
      shouldSearchAdditionalFields,
      locale,
      includeUnavailable,
      page,
    }).then((res) => ({
      items: (res?.data ?? [])
        .map((item) => ({ ...item, kind: normalizeKind(item.type) }))
        .filter((item) => !kinds || kinds.includes(item.kind))
        .filter(
          (item) =>
            item.kind !== 'experience' ||
            !excludedExperienceIds?.includes('' + decodeId(item.id)),
        )
        .map((item) => ({
          id: item.id,
          title: item.title ?? item.displayName ?? item.name,
          kind: pluralKinds
            ? (PLURAL_KINDS[item.kind] ?? item.kind)
            : item.kind,
          description: stripHtml(item.description),
        })),
      // The `kinds`/`excludedExperienceIds` filters above run per page, so a
      // page can come back empty with results still left — hasMore is read off
      // the envelope, never off `items.length`. `meta` is absent on the
      // short-circuit empty response, hence the 1 < 1 fallback.
      hasMore: (res?.meta?.currentPage ?? 1) < (res?.meta?.lastPage ?? 1),
    }));

  return (
    <KoobLocationSearchBase
      fetchResults={fetchResults}
      // `description` is display-only: emitting it would push it into filter
      // URLs and API bodies. Call sites get the same {id, title, kind} they
      // always got.
      onValueChange={(value) =>
        onValueChange?.(
          value ? { id: value.id, title: value.title, kind: value.kind } : null,
        )
      }
      groupOrder={
        pluralKinds ? KIND_ORDER.map((k) => PLURAL_KINDS[k] ?? k) : KIND_ORDER
      }
      groupLabels={groupLabels}
      minLength={SEARCH_MIN_LENGTH}
      {...props}
    />
  );
}
